// SEC2-S3 (brief section 4.4 step 5 and 4.6): the setup screen's reading of what the redeem route answers.
// Pure, so test/ui-terminal-setup-model.test.ts can import it with no DOM.
//
// The server answers EVERY refusal of a code (unknown, expired, revoked, already used, closed business) with
// one identical 400, so that the response is not an oracle for "this code exists but ran out of time". The
// screen must not undo that: it says the same thing for every 400. The approved mockup also draws an
// "expired" variant ("This code ran out of time"); it cannot be shown without making the server answer
// differently, so it is not built (reported as an owner question), and the generic text mentions the 15
// minute life of a code so the common cause is still discoverable.

/** Shown on the setup screen after the server stops recognising a device's credential (brief 4.6). */
export const DISCONNECTED_NOTICE = "This device was disconnected. Ask a manager for a setup code.";

export type RedeemFailureKind = "refused" | "throttled" | "network" | "server";

export interface RedeemFailureView {
  readonly kind: RedeemFailureKind;
  readonly title: string;
  readonly detail: string;
  /** Throttled only: how long the server says to wait, when it said. */
  readonly waitSeconds?: number;
}

export interface RedeemFailureInput {
  readonly status: number;
  readonly body: { error?: string; message?: string } | undefined;
  readonly networkError: boolean;
  /** The Retry-After response header, as text. */
  readonly retryAfter: string | null;
}

/** Whole positive seconds, at most a day. Anything else (an HTTP date, a fraction, a huge number) is not used. */
export function parseRetryAfter(value: string | null): number | undefined {
  if (value === null) return undefined;
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) return undefined;
  const seconds = Number(trimmed);
  return seconds >= 1 && seconds <= 86_400 ? seconds : undefined;
}

export function describeRedeemFailure(input: RedeemFailureInput): RedeemFailureView {
  if (input.networkError || input.status === 0) {
    return {
      kind: "network",
      title: "Could not reach the server.",
      detail: "Check the connection and try again.",
    };
  }
  if (input.status === 429) {
    const waitSeconds = parseRetryAfter(input.retryAfter);
    return {
      kind: "throttled",
      title: "Too many tries.",
      detail: "Wait a few minutes, then try again. Or ask a manager to set up this device for you.",
      ...(waitSeconds === undefined ? {} : { waitSeconds }),
    };
  }
  if (input.status === 400) {
    // Deliberately ignores the body: one screen for every reason the server refused.
    return {
      kind: "refused",
      title: "That code did not work.",
      detail:
        "Check each key. If it still fails, ask a manager for a new code.",
    };
  }
  return {
    kind: "server",
    title: "Something went wrong.",
    detail: "Try again. If it keeps happening, ask a manager.",
  };
}

// ---------------------------------------------------------------------------------------------------------
// UX89 (docs/build-briefs/UX89-name-device-at-setup.md): the "Name this device" step that follows an accepted code.

/** One-tap suggestions on the name step. A tap fills the field; it never saves. */
export const DEVICE_NAME_SUGGESTIONS: readonly string[] = ["Backbar", "Front bar", "Front desk", "Office"];

/** Mirrors src/domain/deviceBinding.ts MAX_DEVICE_NAME_LENGTH and the Add a device form's cap
 * (ui/src/tabletsModel.ts MAX_NAME_LENGTH). */
export const MAX_DEVICE_NAME_LENGTH = 80;

export type DeviceNameCheck =
  | { readonly kind: "unchanged"; readonly name: string }
  | { readonly kind: "save"; readonly name: string }
  | { readonly kind: "invalid"; readonly message: string };

/**
 * What Save should do with the typed text. The same rules the server applies (src/domain/deviceBinding.ts
 * validateDeviceName): trimmed, not blank, at most 80 characters, no control characters. An unchanged name (after
 * trimming) makes no call: the device keeps the name it was given.
 */
export function checkDeviceName(typed: string, current: string): DeviceNameCheck {
  const name = typed.trim();
  if (name.length === 0) return { kind: "invalid", message: "Type a name for this device." };
  if (name.length > MAX_DEVICE_NAME_LENGTH) {
    return {
      kind: "invalid",
      message: `Use ${MAX_DEVICE_NAME_LENGTH} letters or fewer.`,
    };
  }
  if (/\p{Cc}/u.test(name)) {
    return { kind: "invalid", message: "Use letters, numbers and spaces only." };
  }
  return name === current.trim() ? { kind: "unchanged", name: current } : { kind: "save", name };
}

export interface NameFailureView {
  readonly kind: "closed" | "network" | "other";
  readonly title: string;
  readonly detail: string;
}

export interface NameFailureInput {
  readonly status: number;
  readonly body: { error?: string; message?: string } | undefined;
  readonly networkError: boolean;
}

/** The plain line for a refused name. A closed window keeps the name the device has and offers Go to sign in. */
export function describeNameFailure(input: NameFailureInput): NameFailureView {
  if (input.networkError || input.status === 0) {
    return {
      kind: "network",
      title: "Could not reach the server.",
      detail: "Check the connection and tap Save again.",
    };
  }
  if (input.status === 409 && input.body?.error === "device_naming_closed") {
    return {
      kind: "closed",
      title: "This device can no longer rename itself.",
      detail: "It keeps its current name. Ask a manager to change it.",
    };
  }
  return {
    kind: "other",
    title: "The name was not saved.",
    detail: "Tap Save to try again. If it keeps happening, ask a manager.",
  };
}
